import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, ChevronRight, Users, Calendar, Zap, Building2, Award, Star, Heart, Trophy, ArrowRight, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { motion } from "framer-motion";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (user) {
    if (user.role === "PLATFORM_ADMIN") navigate("/admin");
    else if (user.is_coordinator) navigate("/recruitment");
    else if (user.role === "PROVIDER") navigate("/provider");
    else navigate("/dashboard");
  }

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* ── Top Navigation ── */}
      <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur border-b border-slate-100">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-8 h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo({top:0, behavior:"smooth"})}>
            <img src="/logo.png" alt="SetPlay" className="h-9 w-9" />
            <span className="font-heading text-xl font-bold uppercase tracking-tight text-[#0F3D2E]">SetPlay</span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-600">
            <a href="#how-it-works" className="hover:text-[#0F3D2E] transition-colors">How It Works</a>
            <a href="/for-players" className="hover:text-[#0F3D2E] transition-colors">For Players</a>
            <a href="/for-venues" className="hover:text-[#0F3D2E] transition-colors">For Venues</a>
            <a href="/for-coaches" className="hover:text-[#0F3D2E] transition-colors">For Coaches/Sports Professionals</a>
            <a href="#community" className="hover:text-[#0F3D2E] transition-colors">Community</a>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/login")} className="text-sm font-medium" data-testid="nav-login">Login</Button>
            <Button onClick={() => navigate("/login?tab=register")} className="bg-[#0F3D2E] hover:bg-[#1a5c44] text-white rounded-full px-6 text-sm font-medium" data-testid="nav-join">Join Now</Button>
          </div>
        </div>
      </nav>

      {/* ══════════════════════════════════════════════
          SECTION 1: HERO — "Play More. Together."
          ══════════════════════════════════════════════ */}
      <section className="pt-16 relative min-h-[90vh] flex items-center" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1546519638-68e109498ffc?w=1920&q=80" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F3D2E]/90 via-[#0F3D2E]/75 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Copy */}
          <motion.div initial={{opacity:0,x:-30}} animate={{opacity:1,x:0}} transition={{duration:0.6}}>
            <p className="text-[#FBBF24] font-heading uppercase tracking-[0.3em] text-sm mb-4">The Philippines' Community Sports Platform</p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl text-white font-bold leading-[1.05] mb-6" data-testid="hero-headline">
              Play More.<br /><span className="text-[#FBBF24]">Together.</span>
            </h1>
            <p className="text-white/80 text-lg sm:text-xl max-w-lg mb-10 leading-relaxed">
              Find courts. Join games. Build your sports community. Connecting players, coaches, and venues across the Philippines.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
              <Button onClick={() => navigate("/search")} size="lg"
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-full px-8 h-14 text-base font-heading uppercase tracking-wide shadow-lg" data-testid="cta-explore">
                <Search className="w-5 h-5 mr-2" />Explore Games Near Me
              </Button>
              <Button onClick={() => navigate("/list-venue")} variant="outline" size="lg"
                className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white/20 rounded-full px-6 h-14 text-sm font-heading uppercase" data-testid="cta-venue">
                <Building2 className="w-4 h-4 mr-2" />List My Venue/Court
              </Button>
              <Button onClick={() => navigate("/sp/search")} variant="outline" size="lg"
                className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white/20 rounded-full px-6 h-14 text-sm font-heading uppercase" data-testid="cta-officiate">
                <Shield className="w-4 h-4 mr-2" />Officiate a Game
              </Button>
              <Button onClick={() => navigate("/login?tab=register")} variant="outline" size="lg"
                className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white/20 rounded-full px-6 h-14 text-sm font-heading uppercase" data-testid="cta-coach">
                <Award className="w-4 h-4 mr-2" />Become a Coach/Instructor
              </Button>
            </div>
          </motion.div>

          {/* Right: Overlay micro-copy cards */}
          <motion.div className="hidden lg:grid grid-cols-2 gap-4" initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:0.3,duration:0.6}}>
            {[
              {icon: Calendar, label: "Join a Game", desc: "Find pickup games in your area", color: "from-blue-500/80", link: "/search"},
              {icon: MapPin, label: "Book a Court", desc: "Reserve in seconds", color: "from-emerald-500/80", link: "/search"},
              {icon: Award, label: "Train with a Coach", desc: "Level up your skills", color: "from-amber-500/80", link: "/find-coach"},
              {icon: Users, label: "Build Community", desc: "Your team, your league", color: "from-red-500/80", link: "/events"},
            ].map((item, i) => (
              <div key={i} className={`bg-gradient-to-br ${item.color} to-black/40 backdrop-blur-sm rounded-2xl p-5 text-white border border-white/10 hover:scale-105 transition-transform cursor-pointer`}
                onClick={() => navigate(item.link)}>
                <item.icon className="w-7 h-7 mb-2" />
                <p className="font-heading text-sm font-bold uppercase">{item.label}</p>
                <p className="text-xs text-white/70 mt-1">{item.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SECTION 2: HOW IT WORKS — Super Simple
          ══════════════════════════════════════════════ */}
      <section id="how-it-works" className="py-20 sm:py-28 bg-white" data-testid="how-it-works">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 text-center">
          <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}}>
            <p className="text-[#DC2626] font-heading uppercase tracking-[0.2em] text-sm mb-3">Super Simple</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[#0F3D2E] mb-16">How It Works</h2>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 sm:gap-12">
            {[
              {step:"01", icon: Search, title: "Discover", desc: "Find games, venues, and trainers near you."},
              {step:"02", icon: Calendar, title: "Book", desc: "Secure your spot in seconds."},
              {step:"03", icon: Zap, title: "Play", desc: "Show up, compete, connect."},
              {step:"04", icon: Heart, title: "Grow", desc: "Build your sports community."},
            ].map((s, i) => (
              <motion.div key={i} initial={{opacity:0,y:30}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:i*0.1}}
                className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-[#0F3D2E]/5 flex items-center justify-center mx-auto mb-4 relative">
                  <s.icon className="w-7 h-7 text-[#0F3D2E]" />
                  <span className="absolute -top-2 -right-2 w-7 h-7 bg-[#FBBF24] text-[#0F3D2E] rounded-full text-xs font-heading font-bold flex items-center justify-center">{s.step}</span>
                </div>
                <h3 className="font-heading text-xl font-bold text-[#0F3D2E] mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SECTION 3: BUILT FOR PHILIPPINE COMMUNITY SPORTS
          ══════════════════════════════════════════════ */}
      <section id="community" className="py-20 sm:py-28 bg-[#0F3D2E]" data-testid="community-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="text-center mb-14">
            <p className="text-[#FBBF24] font-heading uppercase tracking-[0.2em] text-sm mb-3">Proudly Filipino</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">Built for Philippine<br />Community Sports</h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto">From barangay courts to private sports complexes — SetPlay powers community sports across the Philippines.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {[
              {img:"https://images.unsplash.com/photo-1546519638-68e109498ffc?w=400&q=80", label:"Basketball"},
              {img:"https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?w=400&q=80", label:"Badminton"},
              {img:"https://images.unsplash.com/photo-1554068865-24cecd4e34b8?w=400&q=80", label:"Tennis"},
              {img:"https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=400&q=80", label:"Fitness"},
              {img:"https://images.unsplash.com/photo-1545205597-3d9d02c29597?w=400&q=80", label:"Yoga"},
              {img:"https://images.unsplash.com/photo-1508700115892-45ecd05ae2ad?w=400&q=80", label:"Dance"},
              {img:"https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=400&q=80", label:"Football"},
              {img:"https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=400&q=80", label:"Volleyball"},
            ].map((s, i) => (
              <motion.div key={i} initial={{opacity:0,scale:0.95}} whileInView={{opacity:1,scale:1}} viewport={{once:true}} transition={{delay:i*0.05}}
                onClick={() => navigate(`/search?sport_type=${s.label.toLowerCase()}`)}
                className="relative h-36 sm:h-44 rounded-xl overflow-hidden cursor-pointer group">
                <img src={s.img} alt={s.label} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <p className="absolute bottom-3 left-3 font-heading text-sm text-white font-bold uppercase">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SECTION 4: WHO IS SETPLAY FOR?
          ══════════════════════════════════════════════ */}
      <section id="for-who" className="py-20 sm:py-28 bg-slate-50" data-testid="for-who-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[#0F3D2E] mb-4">Who Is SetPlay For?</h2>
          <p className="text-slate-600 text-lg mb-14">Whether you play, manage, or coach — there's a place for you.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {icon: Users, color: "from-blue-600 to-blue-700", title: "Players", lines: ["Join games", "Meet teammates", "Improve your skills"], cta: "Find a Game", link: "/search"},
              {icon: Building2, color: "from-emerald-600 to-emerald-700", title: "Venues", lines: ["Increase bookings", "Manage schedules", "Grow revenue"], cta: "Partner With Us", link: "/list-venue"},
              {icon: Award, color: "from-amber-500 to-orange-600", title: "Coaches & Trainers", lines: ["Create sessions", "Get discovered", "Earn more"], cta: "Start Coaching", link: "/login?tab=register"},
              {icon: Shield, color: "from-purple-600 to-purple-700", title: "Sports Professionals", lines: ["Officiate a game", "Be a referee", "Generate more income"], cta: "Join as SP", link: "/sp/search"},
            ].map((card, i) => (
              <motion.div key={i} initial={{opacity:0,y:30}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:i*0.15}}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow p-8 text-left group">
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-5`}>
                  <card.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-[#0F3D2E] mb-4">{card.title}</h3>
                <ul className="space-y-2 mb-6">
                  {card.lines.map((l,j) => <li key={j} className="flex items-center gap-2 text-slate-600"><ChevronRight className="w-4 h-4 text-[#FBBF24]" />{l}</li>)}
                </ul>
                <Button onClick={() => navigate(card.link)}
                  className={`w-full bg-gradient-to-r ${card.color} text-white rounded-full font-heading uppercase tracking-wide group-hover:scale-[1.02] transition-transform`}>
                  {card.cta} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SECTION 5: SOCIAL PROOF & FOMO
          ══════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 bg-white" data-testid="social-proof">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 mb-16">
            {[
              {val:"100+", label:"Venues Onboarded", color:"text-[#0F3D2E]"},
              {val:"1,200+", label:"Games Organized", color:"text-[#2563EB]"},
              {val:"500+", label:"Players Joined", color:"text-[#DC2626]"},
              {val:"50+", label:"Coaches Active", color:"text-[#FBBF24]"},
            ].map((s, i) => (
              <motion.div key={i} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:i*0.1}}
                className="text-center">
                <p className={`font-heading text-4xl sm:text-5xl font-bold ${s.color}`}>{s.val}</p>
                <p className="text-sm text-slate-500 mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>

          {/* FOMO */}
          <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}}
            className="bg-gradient-to-r from-[#DC2626] to-[#B91C1C] rounded-2xl p-8 sm:p-12 text-center text-white">
            <h3 className="font-heading text-3xl sm:text-4xl font-bold mb-4">Don't Miss the Game.</h3>
            <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">Join the movement. Be part of the founding community shaping Philippine sports.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => navigate("/login?tab=register")} className="bg-white text-[#DC2626] hover:bg-slate-100 rounded-full px-8 h-12 font-heading uppercase tracking-wide">
                <Star className="w-4 h-4 mr-2" />Join Early Communities
              </Button>
              <Button onClick={() => navigate("/login?tab=register")} variant="outline" className="border-white/40 text-white hover:bg-white/10 rounded-full px-8 h-12 font-heading uppercase tracking-wide">
                <Trophy className="w-4 h-4 mr-2" />Be a Founding Venue
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          SECTION 6: MOVEMENT STATEMENT
          ══════════════════════════════════════════════ */}
      <section className="py-16 sm:py-20 bg-[#0F3D2E]" data-testid="movement-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
          <motion.div initial={{opacity:0}} whileInView={{opacity:1}} viewport={{once:true}}>
            <p className="text-[#FBBF24] font-heading uppercase tracking-[0.2em] text-sm mb-4">Your Team. Your League. Your Community.</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              We're building the future of grassroots sports in the Philippines.
            </h2>
            <p className="text-white/70 text-lg mb-10">SetPlay is more than a platform — it's a movement for every Filipino who loves to play.</p>
            <Button onClick={() => navigate("/login?tab=register")} size="lg"
              className="bg-[#FBBF24] text-[#0F3D2E] hover:bg-[#F59E0B] rounded-full px-10 h-14 text-base font-heading uppercase tracking-wide shadow-lg">
              Join the Movement <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          FOOTER
          ══════════════════════════════════════════════ */}
      <footer className="py-12 bg-[#0a2a1e] text-white/60" data-testid="footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mb-10">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <img src="/logo.png" alt="SetPlay" className="h-8 w-8" />
                <span className="font-heading text-lg font-bold text-white uppercase">SetPlay</span>
              </div>
              <p className="text-sm leading-relaxed">Connecting Filipino communities through sports. One game at a time.</p>
            </div>
            <div>
              <h4 className="font-heading text-sm text-white uppercase tracking-wider mb-3">Platform</h4>
              <div className="space-y-2 text-sm">
                <a href="#how-it-works" className="block hover:text-white transition-colors">How It Works</a>
                <a href="/search" className="block hover:text-white transition-colors">Find Courts</a>
                <a href="/sessions" className="block hover:text-white transition-colors">Browse Sessions</a>
                <a href="/events" className="block hover:text-white transition-colors">Events</a>
              </div>
            </div>
            <div>
              <h4 className="font-heading text-sm text-white uppercase tracking-wider mb-3">Join Us</h4>
              <div className="space-y-2 text-sm">
                <a href="/for-players" className="block hover:text-white transition-colors">For Players</a>
                <a href="/for-venues" className="block hover:text-white transition-colors">For Venues</a>
                <a href="/for-coaches" className="block hover:text-white transition-colors">For Coaches / SP</a>
                <a href="/join-coordinator" className="block hover:text-white transition-colors">Field Coordinator</a>
                <a href="/founding" className="block hover:text-white transition-colors">Founding Circle</a>
              </div>
            </div>
            <div>
              <h4 className="font-heading text-sm text-white uppercase tracking-wider mb-3">Contact</h4>
              <div className="space-y-2 text-sm">
                <p>gamena@setplay.net</p>
                <p>Metro Manila, Philippines</p>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm">Proudly Built for the Philippines by <a href="https://sustek.ai/solutions/soctech" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#FBBF24] underline underline-offset-2 transition-colors">SusTek Labs</a></p>
            <div className="flex gap-4 text-sm">
              <a href="#" className="hover:text-white">Privacy</a>
              <a href="#" className="hover:text-white">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
